// Shared profile/namespace primitives used by the document-bound runtime.
import { APPS, ROUTES } from '../constants/constant';

const PROFILE_TYPES = Object.freeze({
  'odoo17-compat': ['odoo17', 'compat', 'API-KEY'],
  'odoo19-compat': ['odoo19', 'compat', 'API-KEY'],
  'odoo19-native-pilot': ['odoo19', 'native-json2', 'Bearer'],
});
const DEFAULT_PROFILE = 'odoo17-compat';
const profiles = new WeakSet();
const configurations = new WeakSet();
const workflows = new WeakSet();
const own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);
const fail = (message) => {
  throw new Error(`Tile routing: ${message}`);
};
const object = (value) => {
  if (
    !value ||
    typeof value !== 'object' ||
    ![Object.prototype, null].includes(Object.getPrototypeOf(value))
  ) {
    fail('expected a configuration object');
  }
  return value;
};
const identifier = (value, label) => {
  if (typeof value !== 'string' || !/^[A-Za-z0-9_.:-]{1,120}$/.test(value))
    fail(`invalid ${label}`);
  return value;
};
const user = (userId) => {
  if (!Number.isSafeInteger(userId) || userId <= 0) fail('a backend-local user ID is required');
  return userId;
};

// These are the actual DashboardContent / VendorAuthGuard app tags, not permissions.
export const TILE_ROUTES = Object.freeze(
  [
    [APPS.PURCHASE, [ROUTES.PURCHASE_ORDER, `${ROUTES.PURCHASE_ORDER}${ROUTES.URL}`]],
    [APPS.BILL, [ROUTES.BILLS, `${ROUTES.BILLS}${ROUTES.URL}`]],
    [APPS.RECEIPT, [ROUTES.RECEIPT, `${ROUTES.RECEIPT}${ROUTES.URL}`, ROUTES.REPROCESSING]],
    [APPS.MFG, [ROUTES.MANUFACTURING, `${ROUTES.MANUFACTURING}${ROUTES.URL}`]],
    [APPS.PACK, [ROUTES.PACKAGE, ROUTES.PACKAGE_BLP_OVERVIEW]],
    [
      APPS.DISPATCH,
      [
        ROUTES.DISPATCH_BLP_OVERVIEW,
        ROUTES.ADD_MIX_BOX,
        ROUTES.DISPATCH,
        ROUTES.CREATE_DISPATCH,
        `${ROUTES.DISPATCH_DETAILS}${ROUTES.URL}`,
        ROUTES.CONTAINER_THREAD_BOX_ENTRY,
        `${ROUTES.FABRIC_DISPATCH_DETAIL}${ROUTES.URL}`,
        `${ROUTES.YARN_DISPATCH_DETAIL}${ROUTES.URL}`,
      ],
    ],
    [APPS.REPORT_NAME, [ROUTES.REPORTS]],
    [APPS.LOGS, [ROUTES.LOGS, `${ROUTES.LOG_DETAILS}${ROUTES.URL}`]],
    // Marketplace pages are list routes, not record details: each gets a static path.
    [APPS.MARKETPLACE_LABELS, [ROUTES.MARKETPLACE_LABELS, ROUTES.MARKETPLACE_LABELS_TIKTOK]],
  ].flatMap(([tile, paths]) => paths.map((path) => Object.freeze({ tile, path }))),
);
export const TILE_IDS = Object.freeze([...new Set(TILE_ROUTES.map(({ tile }) => tile))]);
const checkTile = (tile) => {
  if (!TILE_IDS.includes(tile)) fail('unknown tile');
};

export function tileForPathname(pathname) {
  if (typeof pathname !== 'string' || /[?#]/.test(pathname)) fail('expected a route pathname');
  const route = TILE_ROUTES.find(({ path }) =>
    path.endsWith(ROUTES.URL)
      ? pathname.startsWith(path.slice(0, -ROUTES.URL.length) + '/') &&
        /^[^/]+$/.test(pathname.slice(path.slice(0, -ROUTES.URL.length).length + 1))
      : pathname === path,
  );
  if (!route) fail('route is not a configured tile');
  return route.tile;
}

export function createBackendProfile(id, settings) {
  if (typeof id !== 'string' || !own(PROFILE_TYPES, id)) fail('unknown backend profile');
  object(settings);
  if (Object.keys(settings).some((key) => !['apiBaseUrl', 'database'].includes(key))) {
    fail('profile accepts only an explicit API base URL and database');
  }
  let url;
  try {
    url = new URL(settings.apiBaseUrl);
  } catch {
    fail('invalid API base URL');
  }
  if (url.protocol !== 'https:' || url.username || url.password || url.search || url.hash)
    fail('API base URL must be HTTPS without credentials, query or fragment');
  const database = identifier(settings.database, 'database');
  const [backend, transport, authScheme] = PROFILE_TYPES[id];
  const profile = Object.freeze({
    id,
    backend,
    transport,
    authScheme,
    apiBaseUrl: `${url.origin}${url.pathname.replace(/\/+$/, '')}`,
    database,
    readOnly: backend !== 'odoo17',
  });
  profiles.add(profile);
  return profile;
}

export function createTileConfiguration({ profiles: selectedProfiles, tiles = {} }) {
  object(selectedProfiles);
  object(tiles);
  const snapshot = {};
  Object.entries(selectedProfiles).forEach(([id, profile]) => {
    if (!profiles.has(profile) || profile.id !== id) fail('profile was not explicitly validated');
    snapshot[id] = profile;
  });
  if (!own(snapshot, DEFAULT_PROFILE)) fail('explicit O17 compatibility profile is required');
  const selections = {};
  Object.entries(tiles).forEach(([tile, id]) => {
    checkTile(tile);
    if (!own(snapshot, id)) fail('selected profile is unavailable');
    // Current vendor services speak compatibility JSON-RPC, not native JSON2.
    if (snapshot[id].transport !== 'compat')
      fail('native pilot transport is not wired for vendor tiles');
    selections[tile] = id;
  });
  const configuration = Object.freeze({
    profiles: Object.freeze(snapshot),
    tiles: Object.freeze(selections),
  });
  configurations.add(configuration);
  return configuration;
}

export function resolveTileProfile(tile, configuration) {
  checkTile(tile);
  if (!configurations.has(configuration)) fail('configuration was not validated');
  return configuration.profiles[
    own(configuration.tiles, tile) ? configuration.tiles[tile] : DEFAULT_PROFILE
  ];
}

export function sessionNamespace(profile, userId) {
  if (!profiles.has(profile)) fail('profile was not validated');
  return JSON.stringify([
    'shaka',
    'vendor',
    'v1',
    profile.backend,
    profile.id,
    profile.transport,
    profile.apiBaseUrl,
    profile.database,
    user(userId),
  ]);
}

export function createWorkflowContext({ tile, configuration, userId, workflowId }) {
  const profile = resolveTileProfile(tile, configuration);
  const context = Object.freeze({
    tile,
    profile,
    userId: user(userId),
    workflowId: identifier(workflowId, 'workflow ID'),
    namespace: sessionNamespace(profile, userId),
  });
  workflows.add(context);
  return context;
}

const checkWorkflow = (context) => {
  if (!workflows.has(context)) fail('workflow was not bound at entry');
};
const workflowNamespace = (context) => {
  checkWorkflow(context);
  return JSON.stringify([context.namespace, context.tile, context.workflowId]);
};

// Called only with the login response from this exact profile. This does not authenticate tokens.
export function bindSession(profile, { userId, token, authScheme }) {
  const namespace = sessionNamespace(profile, userId);
  if (authScheme !== profile.authScheme || typeof token !== 'string' || !token.trim()) {
    fail('session must carry the selected transport authentication scheme');
  }
  return Object.freeze({ namespace, authScheme, token });
}

export function assertSessionForWorkflow(context, session) {
  checkWorkflow(context);
  if (
    !session ||
    session.namespace !== context.namespace ||
    session.authScheme !== context.profile.authScheme ||
    typeof session.token !== 'string' ||
    !session.token.trim()
  )
    fail('session belongs to another backend, database or user');
  return Object.freeze({
    namespace: session.namespace,
    authScheme: session.authScheme,
    token: session.token,
  });
}

export function cacheKey(context, resource) {
  return JSON.stringify(['cache', workflowNamespace(context), identifier(resource, 'resource')]);
}

export function bindRecord(context, model, id) {
  const namespace = workflowNamespace(context);
  identifier(model, 'model');
  if (!Number.isSafeInteger(id) || id <= 0) fail('invalid backend-local record ID');
  return Object.freeze({ namespace, model, id });
}

export function recordIdForWorkflow(context, reference) {
  if (
    !reference ||
    reference.namespace !== workflowNamespace(context) ||
    !Number.isSafeInteger(reference.id) ||
    reference.id <= 0
  )
    fail('record belongs to another workflow');
  return reference.id;
}

export function assertWorkflowWritable(context) {
  checkWorkflow(context);
  if (context.profile.readOnly) fail('O19 writes are disabled while O17 remains authoritative');
}
